const RECENT_KEY = 'oaa:recently-viewed';
const HISTORY_MAX = 12;

function readRecent() {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(value) ? value.filter((h) => typeof h === 'string' && h) : [];
  } catch {
    return [];
  }
}

function writeRecent(handles) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(handles));
  } catch {
    /* private mode / blocked storage — history just doesn't persist */
  }
}

function skeleton() {
  const el = document.createElement('div');
  el.className = 'oaa-recent__skeleton';
  el.setAttribute('role', 'listitem');
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = '<span></span><span></span><span></span><span></span>';
  return el;
}

class OaaRecentlyViewed extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    this.dataset.loaded = 'true';

    this.section = this.closest('.oaa-recent');
    this.track = this.querySelector('[data-recent-track]');
    this.status = this.querySelector('[data-recent-status]');
    this.nav = this.section?.querySelector('[data-recent-nav]');
    this.prev = this.section?.querySelector('[data-rail-prev]');
    this.next = this.section?.querySelector('[data-rail-next]');

    const current = this.dataset.currentHandle || '';
    const limit = Math.max(1, Number(this.dataset.limit) || 5);

    const history = readRecent();
    const toShow = history.filter((handle) => handle !== current).slice(0, limit);

    if (current) {
      writeRecent([current, ...history.filter((h) => h !== current)].slice(0, HISTORY_MAX));
    }

    if (!toShow.length || !this.track) return;

    toShow.forEach(() => this.track.append(skeleton()));
    this.section?.removeAttribute('hidden');

    const cards = await Promise.all(toShow.map((handle) => this.fetchCard(handle)));
    const resolved = cards.filter(Boolean);

    this.track.replaceChildren();
    resolved.forEach((card) => {
      card.setAttribute('role', 'listitem');
      this.track.append(card);
    });

    if (!resolved.length) {
      this.section?.setAttribute('hidden', '');
      return;
    }

    if (this.status) {
      this.status.textContent = resolved.length + ' recently viewed ' + (resolved.length === 1 ? 'product' : 'products');
    }

    this.bindRail();
  }

  async fetchCard(handle) {
    try {
      const url = '/products/' + encodeURIComponent(handle) + '?section_id=oaa-recently-viewed-card';
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return null;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('[data-recent-card]');
    } catch {
      return null;
    }
  }

  bindRail() {
    if (!this.track || !this.nav) return;

    const step = () => {
      const first = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
      return first ? first.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
    };

    const overflows = () => this.track.scrollWidth - this.track.clientWidth > 2;

    const sync = () => {
      if (!overflows()) {
        this.nav.setAttribute('hidden', '');
        return;
      }
      this.nav.removeAttribute('hidden');
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      const atStart = Math.abs(x) <= 2;
      const atEnd = Math.abs(Math.abs(x) - max) <= 2;
      this.prev?.toggleAttribute('disabled', atStart);
      this.next?.toggleAttribute('disabled', atEnd);
    };

    const dir = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step() * dir, behavior: 'smooth' }));
    this.next?.addEventListener('click', () => this.track.scrollBy({ left: step() * dir, behavior: 'smooth' }));

    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(sync);
    }, { passive: true });

    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(sync);
      this._ro.observe(this.track);
    } else {
      window.addEventListener('resize', sync, { passive: true });
    }

    sync();
  }

  disconnectedCallback() {
    this._ro?.disconnect();
    cancelAnimationFrame(this._raf);
  }
}

if (!customElements.get('oaa-recently-viewed')) {
  customElements.define('oaa-recently-viewed', OaaRecentlyViewed);
}